import type {
  SpeechProvider,
  SpeechProviderCallbacks,
  SpeechProviderContext,
  SpeechSession,
  SpeechStopOptions,
  SpeechError,
} from './types';
import {
  createAudioContext,
  startCapture,
  isCaptureSupported,
  CaptureError,
  type CaptureErrorCode,
  type AudioCapture,
} from './shared/audioCapture';
import { connectElevenLabsStream, type ElevenLabsStreamHandle } from './elevenlabs/elevenLabsStreamClient';
import { getSttToken, SttTokenError } from './sttTokenService';
import {
  registerActiveSpeechSession,
  clearActiveSpeechSession,
  stopActiveSpeechSession,
} from './speechSessionRegistry';
import { addSentryBreadcrumb, captureException } from '@/config/sentry.config';
import { STT_CONFIG } from '@/config/appConfig';

// ElevenLabs doesn't send a "your session is now confirmed" signal gated behind
// anything but the WS handshake itself, but a bad/expired token can still open
// the socket and then sit silent — same risk AssemblyAI's Begin-timeout guards
// against, so this plays the same role.
const SESSION_STARTED_TIMEOUT_MS = STT_CONFIG.beginTimeoutMs;
// No server ack exists for "I've flushed the trailing segment" the way
// AssemblyAI's Termination message works — completeStop always waits this out.
const FINALIZE_TIMEOUT_MS = STT_CONFIG.finalizeTimeoutMs;
const MAX_SESSION_MS = STT_CONFIG.maxSessionMs;
const UNAVAILABLE_MESSAGE = 'Voice input is unavailable right now. Please type your question.';
const SILENCE_MESSAGE = "We didn't catch that. Try again.";

// The documented error-family message types (see elevenLabsStreamClient.ts) that
// mean the session is unusable and must be torn down. `commit_throttled` and
// `insufficient_audio_activity` are deliberately excluded: both are transient/
// informational (a quiet pause, a commit sent too soon after the last one) and
// killing the session over either would make normal pauses in speech fatal.
const FATAL_SERVER_ERRORS = new Set([
  'error',
  'auth_error',
  'quota_exceeded',
  'unaccepted_terms',
  'rate_limited',
  'queue_overflow',
  'resource_exhausted',
  'session_time_limit_exceeded',
  'input_error',
  'invalid_request',
  'chunk_size_exceeded',
  'transcriber_error',
]);

/**
 * One ElevenLabs Scribe v2 Realtime session. Structurally mirrors
 * `AssemblyAISession` in `assemblyAIProvider.ts` — same idle -> starting ->
 * connecting -> listening -> finalizing -> idle/error state machine, same
 * teardown ordering — adapted to this provider's own message protocol
 * (`session_started`/`committed_transcript` instead of `Begin`/`Turn`, `flush()`
 * instead of `terminate()`). Reuses the same shared mic-capture pipeline
 * (`shared/audioCapture.ts`) since both providers want 16kHz PCM16 chunks; only
 * the wire format each expects for those chunks differs.
 */
class ElevenLabsSession implements SpeechSession {
  private readonly callbacks: SpeechProviderCallbacks;
  private readonly getAccessToken: () => Promise<string>;
  private readonly registryEntry: { providerId: 'elevenlabs'; stop: () => Promise<void> };

  private ctx: AudioContext | null = null;
  private capture: AudioCapture | null = null;
  private stream: ElevenLabsStreamHandle | null = null;
  private sessionStartedTimer: ReturnType<typeof setTimeout> | null = null;
  private finalizeTimer: ReturnType<typeof setTimeout> | null = null;
  private capTimer: ReturnType<typeof setTimeout> | null = null;
  private lastCommittedText: string | null = null;
  private silentStop = false;
  private torn = false;
  private stopping = false;
  private resolveStop: (() => void) | null = null;

  constructor(callbacks: SpeechProviderCallbacks, getAccessToken: () => Promise<string>) {
    this.callbacks = callbacks;
    this.getAccessToken = getAccessToken;
    this.registryEntry = { providerId: 'elevenlabs', stop: () => this.teardown() };
  }

  async start(): Promise<void> {
    // Same doc-#1 mitigation as AssemblyAI: evict whatever session (from either
    // provider) the registry currently holds before starting a new one.
    await stopActiveSpeechSession();
    this.torn = false;
    this.stopping = false;

    this.callbacks.onStateChange('starting', 'Requesting microphone access…');
    // Must stay synchronous relative to the user gesture — see audioCapture.ts's
    // createAudioContext() doc comment (iOS AudioContext-in-click-handler rule).
    this.ctx = createAudioContext();

    // Mic capture (permission prompt + worklet load) and token issuance
    // (Auth0 + our backend's STT token) don't depend on each other — running
    // them concurrently instead of one after the other cuts time-to-listening
    // to roughly whichever is slower, instead of their sum.
    const capturePromise = startCapture(this.ctx, (pcm) => this.stream?.send(pcm));
    const tokenPromise = this.getAccessToken().then((accessToken) => getSttToken(accessToken, 'elevenlabs'));

    let token: string;
    try {
      const [capture, tokenResponse] = await Promise.all([capturePromise, tokenPromise]);
      this.capture = capture;
      token = tokenResponse.token;
    } catch (err) {
      // Promise.all rejects as soon as either side does, without waiting for
      // (or cancelling) the other. If capture is the one still in flight and
      // it later succeeds, nothing else would ever stop that MediaStream —
      // `this.capture` was never assigned, so teardown()'s `this.capture?.stop()`
      // is a no-op. Attach cleanup directly to the promise itself so a mic
      // grant arriving after we've already given up doesn't leak.
      capturePromise.then((c) => c.stop()).catch(() => undefined);
      await this.teardown();
      this.fail(mapStartError(err));
      return;
    }
    if (this.stopping || this.torn) {
      void this.completeStop();
      return;
    }

    this.callbacks.onStateChange('starting', 'Connecting…');
    registerActiveSpeechSession(this.registryEntry);

    await new Promise<void>((resolveConnected) => {
      this.stream = connectElevenLabsStream(token, {
        onSessionStarted: () => {
          if (this.sessionStartedTimer) {
            clearTimeout(this.sessionStartedTimer);
            this.sessionStartedTimer = null;
          }
          if (!this.torn && !this.stopping) {
            this.callbacks.onStateChange('listening');
            addSentryBreadcrumb({
              message: '[STT] elevenlabs listening',
              category: 'stt',
              data: { sampleRate: this.ctx?.sampleRate },
            });
            this.capTimer = setTimeout(() => this.finalize(true), MAX_SESSION_MS);
          }
          resolveConnected();
        },
        onCommitted: (text) => this.handleCommitted(text),
        onServerMessage: (messageType, message) => {
          if (!FATAL_SERVER_ERRORS.has(messageType)) {
            addSentryBreadcrumb({
              message: `[STT] elevenlabs server message: ${messageType}`,
              category: 'stt',
              data: { message },
            });
            return;
          }
          void this.teardown().then(() =>
            this.fail({ code: 'network-or-service', message: UNAVAILABLE_MESSAGE, cause: message }),
          );
        },
        onError: (err) => {
          captureException(err instanceof Error ? err : new Error('[STT] elevenlabs ws error'), {
            provider: 'elevenlabs',
          });
        },
        onClose: () => {
          if (this.torn) return; // our own teardown already closed it — expected
          void this.teardown().then(() => this.fail({ code: 'network-or-service', message: UNAVAILABLE_MESSAGE }));
          resolveConnected();
        },
      });

      // Never enter "listening" without session_started — with a bad/expired
      // token the socket can still open and we'd sit there streaming audio into
      // the void with the UI stuck saying "Connecting…" forever.
      this.sessionStartedTimer = setTimeout(() => {
        void this.teardown().then(() => this.fail({ code: 'network-or-service', message: UNAVAILABLE_MESSAGE }));
        resolveConnected();
      }, SESSION_STARTED_TIMEOUT_MS);
    });
  }

  stop(options?: SpeechStopOptions): Promise<void> {
    if (this.torn) return Promise.resolve();
    return new Promise((resolve) => {
      this.resolveStop = resolve;
      this.finalize(options?.silent ?? false);
    });
  }

  private handleCommitted(text: string): void {
    const trimmed = text.trim();
    if (!trimmed) return;
    // Unlike AssemblyAI, ElevenLabs' committed_transcript is never re-emitted as
    // a later revision of the same segment — no `replacesPreviousText` needed.
    this.callbacks.onFinal({ text: trimmed });
    this.lastCommittedText = trimmed;
  }

  private finalize(silent: boolean): void {
    if (this.torn || this.stopping) return;
    this.stopping = true;
    this.silentStop = silent;
    addSentryBreadcrumb({ message: `[STT] elevenlabs finalize (silent=${silent})`, category: 'stt' });
    this.callbacks.onStateChange('finalizing');
    this.stream?.flush();
    // No server ack for "flushed" exists here (see FINALIZE_TIMEOUT_MS doc
    // comment) — this timer is the only path to completeStop() on a normal stop.
    this.finalizeTimer = setTimeout(() => void this.completeStop(), FINALIZE_TIMEOUT_MS);
  }

  private async completeStop(): Promise<void> {
    if (this.torn) return;
    if (this.finalizeTimer) {
      clearTimeout(this.finalizeTimer);
      this.finalizeTimer = null;
    }
    const hadNoSpeech = this.lastCommittedText === null;
    const silent = this.silentStop;
    await this.teardown();
    if (!silent && hadNoSpeech) {
      this.fail({ code: 'silence', message: SILENCE_MESSAGE });
      return;
    }
    this.callbacks.onStateChange('idle');
    this.resolveStop?.();
    this.resolveStop = null;
  }

  private fail(error: SpeechError): void {
    this.callbacks.onError(error);
    this.callbacks.onStateChange('error');
    this.resolveStop?.();
    this.resolveStop = null;
  }

  /**
   * Idempotent teardown, same ordering as AssemblyAISession.teardown(): detach WS
   * handlers before closing, then stop the mic tracks (turns off the browser's
   * mic-in-use indicator), then close the AudioContext, then clear every timer.
   */
  private async teardown(): Promise<void> {
    if (this.torn) return;
    this.torn = true;
    if (this.sessionStartedTimer) {
      clearTimeout(this.sessionStartedTimer);
      this.sessionStartedTimer = null;
    }
    if (this.finalizeTimer) {
      clearTimeout(this.finalizeTimer);
      this.finalizeTimer = null;
    }
    if (this.capTimer) {
      clearTimeout(this.capTimer);
      this.capTimer = null;
    }
    this.stream?.close();
    this.capture?.stop();
    if (this.ctx && this.ctx.state !== 'closed') {
      await this.ctx.close().catch(() => undefined);
    }
    clearActiveSpeechSession(this.registryEntry);
  }
}

function mapStartError(err: unknown): SpeechError {
  if (err instanceof CaptureError) {
    return { code: err.code, message: captureErrorMessage(err.code), cause: err };
  }
  if (err instanceof SttTokenError) {
    return { code: 'network-or-service', message: UNAVAILABLE_MESSAGE, cause: err };
  }
  return { code: 'network-or-service', message: UNAVAILABLE_MESSAGE, cause: err };
}

function captureErrorMessage(code: CaptureErrorCode): string {
  switch (code) {
    case 'permission-denied':
      return 'Microphone access was blocked.';
    case 'no-microphone':
      return 'No microphone was found on this device.';
    case 'microphone-busy':
      return 'The microphone is in use by another application.';
    default:
      return UNAVAILABLE_MESSAGE;
  }
}

export const elevenLabsProvider: SpeechProvider = {
  id: 'elevenlabs',
  isSupported(): boolean {
    return isCaptureSupported();
  },
  createSession(callbacks: SpeechProviderCallbacks, context: SpeechProviderContext): SpeechSession {
    if (!context.getAccessToken) {
      throw new Error('[STT] elevenLabsProvider.createSession requires context.getAccessToken');
    }
    return new ElevenLabsSession(callbacks, context.getAccessToken);
  },
};
